import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';

import { Link } from '@fluentui/react/lib/Link';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  useClientContext,
  InstrumentationServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import { sendAnalyticsData, sendLeadsTrackingData, THEME_MODE_KEY_MAPPING } from '@bingads-webui-campaign-react/smart-component-util';

import { EmailForm } from './components/email-form';
import { PromptDialog } from './components/prompt-dialog';
import { BookingSteps } from './components/booking-steps';
import { SmartActionButtonTypes } from './utils/smart-action-types';

const getStyles = (components, { palette = {} }) => ({
  actionButton: {
    ...components.primaryButton,
    textAlign: 'center',
    textDecoration: 'none',
    display: 'block',
    margin: '0 auto',
    width: 'fit-content',
    maxWidth: 200,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  iconButton: {
    ...components.section,
    textAlign: 'center',
    '& a': {
      height: 'fit-content',
      width: 'fit-content',
      display: 'inline-block',
      '&:focus': {
        outline: `1px solid ${palette.outline}`,
      },
      '&:hover': {
        backgroundColor: palette.secondaryBackground,
      },
    },
  },
  icon: {
    width: 32,
    height: 32,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 12,
    ...(palette.mode === THEME_MODE_KEY_MAPPING.Dark ? { filter: 'brightness(0) invert(1)' } : {}),
  },
  iconText: {
    color: palette.secondaryText,
    textDecoration: 'underline',
    display: 'block',
    fontSize: 16,
    lineHeight: '24px',
  },
});

const UnStyledSmartActionButtonView = ({
  actionType,
  text,
  target,
  id,
  isFormLink,
  mailService,
  i18n,
  classes,
  className,
  useIconButton,
  icon,
  disabled,
  isEditMode,
  bookingProps,
  userLocale,
  pilots,
}) => {
  const [hideDialog, setHideDialog] = useState(true);
  const [hidePromptDialog, setHidePromptDialog] = useState(true);
  const [showError, setShowError] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const { isRuntime, isMobile } = useClientContext();
  const logger = useContext(InstrumentationServiceContext);

  const host = window.location.hostname;
  const showWarning = !isRuntime;

  const onClickActionButton = useCallback(() => {
    if (isFormLink && hideDialog) {
      // open dialog, not track
      setHideDialog(false);
    } else if (
      actionType === SmartActionButtonTypes.BookAppointment.name
      && pilots.isSmartPageBookAppointmentEnabled && !bookingProps.isExternalLink
    ) {
      setShowBookingModal(true);
    } else if (isRuntime) {
      // only trigger when smart page in runtime
      sendAnalyticsData(actionType);
      sendLeadsTrackingData();
    }

    if (isRuntime) {
      logger.logTrace(`CTA|openDialog|${host}|${actionType}`);
    }
  }, [
    isFormLink, hideDialog, actionType, pilots,
    isRuntime, logger, host, bookingProps,
  ]);

  const onSubmitForm = useCallback(({
    name,
    phone,
    mail,
    message,
  }) => {
    setRequestSent(true);
    mailService.sendMail({
      mailType: actionType,
      userName: name,
      userMail: mail,
      userPhone: phone,
      message,
      userLocale,
    }).then(() => {
      setHideDialog(true);
      setHidePromptDialog(false);
    }).catch((res) => {
      setShowError(true);
      logger.logError('Send email failed', res);
    }).finally(() => {
      setRequestSent(false);
    });
    // only trigger when smart page in runtime
    if (isRuntime) {
      sendAnalyticsData(actionType);
      sendLeadsTrackingData();
      logger.logTrace(`CTA|submitForm|${host}|${actionType}`);
    }
  }, [actionType, host, isRuntime, logger, mailService, userLocale]);

  const onCancelForm = useCallback(() => {
    setHideDialog(true);
  }, []);

  const onClosePromptDialog = useCallback(() => {
    setHidePromptDialog(true);
  }, []);

  const onCloseBookingModal = useCallback((e, isComplete) => {
    setShowBookingModal(false);

    if (isComplete) {
      setHidePromptDialog(false);
    }
  }, []);

  const IconContainer = (disabled || isEditMode) ? 'span' : 'a';

  const pureButton = useMemo(() => {
    if (isEditMode) {
      return (
        <span
          className={
            classNames(
              'action-button',
              classes.actionButton,
              className
            )
          }
          id={id}
        >
          {text}
        </span>
      );
    }
    return (
      <Link
        className={
          classNames(
            'action-button',
            classes.actionButton,
            className
          )
        }
        href={target}
        onClick={onClickActionButton}
        role="button"
        target={isFormLink ? '_self' : '_blank'}
        rel="noopener noreferrer"
        aria-label={text}
        id={id}
        disabled={disabled}
      >
        {text}
      </Link>
    );
  }, [
    isEditMode, className, classes, disabled,
    onClickActionButton, text, id, isFormLink, target,
  ]);

  return (
    <React.Fragment>
      {
        !useIconButton && pureButton
      }
      {
        useIconButton &&
        <div id={id} className={classNames(classes.iconButton, className)}>
          <IconContainer href={target} onClick={(disabled || isEditMode) ? _.noop : onClickActionButton} target="_blank" rel="noopener noreferrer">
            <img className={classes.icon} src={icon} alt="" />
            <span className={classes.iconText}>{text}</span>
          </IconContainer>
        </div>
      }
      <EmailForm
        title={text}
        hideDialog={hideDialog}
        showError={showError}
        showWarning={showWarning}
        requestSent={requestSent}
        onSubmit={onSubmitForm}
        onCancel={onCancelForm}
        i18n={i18n}
      />
      {
        showBookingModal && (
          <BookingSteps
            isOpen
            i18n={i18n}
            onClose={onCloseBookingModal}
            bookingProps={bookingProps}
            isRuntime={isRuntime}
            isMobile={isMobile}
            logger={logger}
            userLocale={userLocale}
          />
        )
      }
      <PromptDialog
        hideDialog={hidePromptDialog}
        onCancel={onClosePromptDialog}
        i18n={i18n}
      />
    </React.Fragment>
  );
};

UnStyledSmartActionButtonView.propTypes = {
  actionType: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  target: PropTypes.string,
  id: PropTypes.string,
  isFormLink: PropTypes.bool,
  mailService: PropTypes.shape({
    sendMail: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    actionButton: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  useIconButton: PropTypes.bool,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool,
  bookingProps: PropTypes.shape({
    smartPageId: PropTypes.string,
    availableBookingHours: PropTypes.arrayOf(PropTypes.shape({
      day: PropTypes.string,
      isOpenAllDay: PropTypes.bool,
      timeSlots: PropTypes.arrayOf(PropTypes.shape({})),
      isValid: PropTypes.bool,
    })),
    email: PropTypes.string,
    landingLink: PropTypes.string,
    businessName: PropTypes.string,
  }).isRequired,
  pilots: PropTypes.shape({}),
  userLocale: PropTypes.string.isRequired,
};

UnStyledSmartActionButtonView.defaultProps = {
  target: '',
  id: undefined,
  isFormLink: false,
  className: '',
  useIconButton: false,
  icon: '',
  disabled: false,
  isEditMode: false,
  pilots: {},
};

export const SmartActionButtonView = withDefaultStyles(
  UnStyledSmartActionButtonView,
  getStyles
);
